import { getTaxRateById } from '@/services/taxRateCache'

const DEFAULT_CURRENCY_SYMBOL = '€'

export const DEFAULT_COLUMNS = [
  { key: 'description', label: 'Beschreibung' },
  { key: 'quantity', label: 'Menge' },
  { key: 'unitPrice', label: 'Einzelpreis' },
  { key: 'tax', label: 'MwSt' },
  { key: 'taxAmount', label: 'MwSt Betrag' },
  { key: 'total', label: 'Gesamt' },
]

const roundCurrency = (value) => {
  if (!isFinite(value)) return 0
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100
}

const normalizeNumber = (raw) => {
  if (raw === null || raw === undefined || raw === '') return 0
  const numeric = typeof raw === 'string' ? raw.replace(',', '.') : raw
  const value = parseFloat(numeric)
  return isFinite(value) ? value : 0
}

const formatCurrency = (value, currencySymbol = DEFAULT_CURRENCY_SYMBOL) => {
  return `${roundCurrency(value).toFixed(2)}${currencySymbol}`
}

const formatTaxRateLabel = (rate) => {
  const numericRate = normalizeNumber(rate)
  if (!Number.isFinite(numericRate) || numericRate <= 0) {
    return ''
  }
  const rounded = Math.round(numericRate * 100) / 100
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(2)}%`
}

const defaultResolveTaxRate = async (taxRateId) => {
  return await getTaxRateById(taxRateId)
}

const unwrapDataValues = (raw) => {
  if (raw && typeof raw === 'object' && raw.dataValues) {
    return raw.dataValues
  }
  return raw
}

const normalizeTaxRateId = (value) => {
  const unwrapped = unwrapDataValues(value)
  if (unwrapped === '' || unwrapped === null || unwrapped === undefined) return null
  if (typeof unwrapped === 'object') {
    if (unwrapped.id !== undefined && unwrapped.id !== null && unwrapped.id !== '') {
      return unwrapped.id
    }
    if ('data' in unwrapped) {
      return normalizeTaxRateId(unwrapped.data)
    }
  }
  return unwrapped
}

const normalizeResolvedTaxRate = (raw, fallbackId) => {
  const unwrapped = unwrapDataValues(raw)
  if (!unwrapped || typeof unwrapped !== 'object') return null
  const normalizedRate = normalizeNumber(unwrapped.rate)
  return {
    id: unwrapped.id || fallbackId || null,
    rate: normalizedRate,
    displayName: unwrapped.displayName || unwrapped.name || `${normalizedRate}%`,
  }
}

const getResolvedTaxRateFromField = (field, fallbackId) => {
  if (!field || typeof field !== 'object') return null
  return (
    normalizeResolvedTaxRate(field.resolvedTaxRate, fallbackId) ||
    normalizeResolvedTaxRate(field.data, fallbackId)
  )
}

/**
 * Erzeugt ein zentrales Tabellenobjekt aus Positionsdaten (Billing-Items).
 *
 * @param {Array<Object>} items
 * @param {Object} options
 * @param {Function} [options.resolveTaxRate] async (taxRateId, item) => { id, rate, displayName }
 * @param {String} [options.currencySymbol]
 * @returns {Promise<Object>}
 */
export const buildBillingTable = async (items = [], options = {}) => {
  const {
    resolveTaxRate = defaultResolveTaxRate,
    currencySymbol = DEFAULT_CURRENCY_SYMBOL,
  } = options

  const rows = []
  const taxBreakdownMap = new Map()
  const taxRateCache = new Map()

  let totalNet = 0
  let totalGross = 0
  let totalTax = 0

  const getResolvedTaxRate = async (item) => {
    if (!item) return null

    if (item.resolvedTaxRate && typeof item.resolvedTaxRate === 'object') {
      return {
        id: item.resolvedTaxRate.id || item.taxRateId || null,
        rate: normalizeNumber(item.resolvedTaxRate.rate),
        displayName:
          item.resolvedTaxRate.displayName ||
          item.resolvedTaxRate.name ||
          `${normalizeNumber(item.resolvedTaxRate.rate)}%`,
      }
    }

    if (!item.taxRateId) return null

    if (taxRateCache.has(item.taxRateId)) {
      return taxRateCache.get(item.taxRateId)
    }

    const resolved = await resolveTaxRate(item.taxRateId, item)
    if (resolved) {
      const normalized = {
        id: resolved.id || item.taxRateId,
        rate: normalizeNumber(resolved.rate),
        displayName:
          resolved.displayName || resolved.name || `${normalizeNumber(resolved.rate)}%`,
      }
      taxRateCache.set(item.taxRateId, normalized)
      return normalized
    }

    taxRateCache.set(item.taxRateId, null)
    return null
  }

  for (const rawItem of items) {
    const quantity = normalizeNumber(rawItem?.quantity)
    const unitPriceGross = normalizeNumber(rawItem?.unitPrice)
    const unitLabel = rawItem?.unit || ''
    const description =
      rawItem?.description?.trim?.() ||
      (rawItem?.label?.trim?.() || 'Position')

    const gross = roundCurrency(quantity * unitPriceGross)
    const resolvedTaxRate = await getResolvedTaxRate(rawItem)
    const normalizedRate = resolvedTaxRate ? normalizeNumber(resolvedTaxRate.rate) : null
    const taxRateId = resolvedTaxRate?.id || normalizeTaxRateId(rawItem?.taxRateId) || null
    const hasTax = normalizedRate !== null && normalizedRate > 0
    const effectiveRate = hasTax ? normalizedRate : 0
    const taxDisplayName = hasTax ? formatTaxRateLabel(effectiveRate) : ''

    const net = hasTax ? roundCurrency(gross / (1 + effectiveRate / 100)) : gross
    const taxAmount = roundCurrency(gross - net)
    const unitPriceNet = quantity !== 0 ? roundCurrency(net / quantity) : unitPriceGross

    totalNet += net
    totalGross += gross
    totalTax += taxAmount

    const breakdownKey = taxRateId || `rate-${effectiveRate}`
    if (!taxBreakdownMap.has(breakdownKey)) {
      taxBreakdownMap.set(breakdownKey, {
        taxRateId,
        rate: effectiveRate,
        displayName: taxDisplayName,
        net: 0,
        tax: 0,
        gross: 0,
      })
    }

    const breakdownEntry = taxBreakdownMap.get(breakdownKey)
    breakdownEntry.net = roundCurrency(breakdownEntry.net + net)
    breakdownEntry.tax = roundCurrency(breakdownEntry.tax + taxAmount)
    breakdownEntry.gross = roundCurrency(breakdownEntry.gross + gross)

    rows.push({
      description,
      quantity: {
        value: quantity,
        unit: unitLabel,
        display: unitLabel ? `${quantity} ${unitLabel}` : `${quantity}`,
      },
      unitPrice: {
        net: unitPriceNet,
        gross: unitPriceGross,
        displayGross: formatCurrency(unitPriceGross, currencySymbol),
        displayNet: formatCurrency(unitPriceNet, currencySymbol),
      },
      tax: {
        rate: effectiveRate,
        amount: taxAmount,
        displayAmount: taxAmount > 0 ? formatCurrency(taxAmount, currencySymbol) : '',
        displayName: taxDisplayName,
      },
      total: {
        net,
        gross,
        display: formatCurrency(gross, currencySymbol),
      },
    })
  }

  const summary = {
    net: roundCurrency(totalNet),
    tax: roundCurrency(totalTax),
    gross: roundCurrency(totalGross),
    display: {
      net: formatCurrency(totalNet, currencySymbol),
      tax: formatCurrency(totalTax, currencySymbol),
      gross: formatCurrency(totalGross, currencySymbol),
    },
  }

 const taxBreakdown = Array.from(taxBreakdownMap.values())
    .filter((entry) => normalizeNumber(entry.rate) > 0 && roundCurrency(entry.tax) > 0)
    .map((entry) => ({
      ...entry,
      display: {
        net: formatCurrency(entry.net, currencySymbol),
        tax: formatCurrency(entry.tax, currencySymbol),
        gross: formatCurrency(entry.gross, currencySymbol),
      },
    }))

  return {
    columns: DEFAULT_COLUMNS,
    currencySymbol,
    rows,
    summary,
    taxBreakdown,
  }
}

const deriveLabel = (field, fallback) => {
  if (field?.label) return field.label
  const normalized = fallback?.replace?.(/_/g, ' ')
  if (!normalized) return 'Position'
  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

const mapFinancialFieldToItems = (fieldName, field, receipt, fieldsSet) => {
  const items = []
  if (!field) return items
  if (typeof fieldName === 'string' && fieldName.endsWith('_taxRate')) {
    // Skip helper fields that only carry the tax rate selection
    return items
  }

  const taxRateFieldName = `${fieldName}_taxRate`
  const taxRateField = receipt?.data?.[taxRateFieldName]
  const normalizedFieldTaxRateId = normalizeTaxRateId(field.taxRateId)
  const normalizedTaxRateFieldId = normalizeTaxRateId(taxRateField?.data)
  const baseTaxRateId = normalizedFieldTaxRateId ?? normalizedTaxRateFieldId ?? null
  const baseResolvedRate =
    getResolvedTaxRateFromField(field, baseTaxRateId) ||
    getResolvedTaxRateFromField(taxRateField, baseTaxRateId) ||
    null

  const pushItem = (quantity, unitPrice, unit, description, entryTaxRateId, entryResolvedRate) => {
    const qty = normalizeNumber(quantity)
    const price = normalizeNumber(unitPrice)
    if (qty === 0 && price === 0) return
    const normalizedEntryTaxRateId = normalizeTaxRateId(entryTaxRateId)
    const resolvedRate =
      normalizeResolvedTaxRate(entryResolvedRate, normalizedEntryTaxRateId ?? baseTaxRateId) ||
      baseResolvedRate ||
      null
    const taxRateId = normalizedEntryTaxRateId ?? resolvedRate?.id ?? baseTaxRateId ?? null
    items.push({
      description: description || deriveLabel(field, fieldName),
      quantity: qty,
      unit: unit || field.unit || '',
      unitPrice: price,
      taxRateId,
      resolvedTaxRate: resolvedRate,
    })
    fieldsSet?.add(fieldName)
  }

  if (Array.isArray(field.data)) {
    field.data.forEach((entry, index) => {
      pushItem(
        entry.quantity ?? 1,
        entry.unitPrice ?? entry.amount ?? 0,
        entry.unit,
        entry.description || `${deriveLabel(field, fieldName)} ${index + 1}`,
        entry.taxRateId,
        entry.resolvedTaxRate
      )
    })
    return items
  }

  if (field.data !== undefined && field.data !== null && field.data !== '') {
    if (field.unit && field.pricePerUnit) {
      pushItem(
        field.data,
        field.pricePerUnit,
        field.unit,
        deriveLabel(field, fieldName),
        baseTaxRateId,
        baseResolvedRate
      )
    } else {
      pushItem(1, field.data, field.unit, deriveLabel(field, fieldName), baseTaxRateId, baseResolvedRate)
    }
    return items
  }

  if (field.quantity !== undefined || field.unitPrice !== undefined) {
    pushItem(
      field.quantity ?? 1,
      field.unitPrice ?? 0,
      field.unit,
      deriveLabel(field, fieldName),
      baseTaxRateId,
      baseResolvedRate
    )
  }

  return items
}

export const buildReceiptTables = async (receipt, options = {}) => {
  if (!receipt || !receipt.data) {
    const emptyTable = await buildBillingTable([], options)
    return {
      repeaterTables: {},
      financialTable: emptyTable,
      combinedTable: emptyTable,
      fieldsUsedInCombined: [],
    }
  }

  const {
    includeRepeaterInFinancial = false,
    resolveTaxRate = defaultResolveTaxRate,
    currencySymbol = DEFAULT_CURRENCY_SYMBOL,
  } = options

  const repeaterTables = {}
  const repeaterItems = []
  const financialItems = []
  const fieldsUsed = new Set()

  for (const [fieldName, field] of Object.entries(receipt.data)) {
    if (!field || !field.category) continue
    if (typeof fieldName === 'string' && fieldName.endsWith('_taxRate')) continue

    if (field.category === 'receipt' && field.type === 'repeater' && Array.isArray(field.data)) {
      const normalizedItems = []

      field.data.forEach((item, index) => {
        const quantity = normalizeNumber(item?.quantity ?? 1)
        const unitPrice = normalizeNumber(item?.unitPrice ?? 0)
        if (quantity === 0 && unitPrice === 0) return

        const entryTaxRateId = normalizeTaxRateId(item?.taxRateId)
        const resolvedRate =
          normalizeResolvedTaxRate(item?.resolvedTaxRate, entryTaxRateId) || null
        const taxRateId = entryTaxRateId || resolvedRate?.id || null

        normalizedItems.push({
          description: item?.description || `${deriveLabel(field, fieldName)} ${index + 1}`,
          quantity,
          unit: item?.unit || '',
          unitPrice,
          taxRateId,
          resolvedTaxRate: resolvedRate,
        })
      })

      if (normalizedItems.length > 0) {
        fieldsUsed.add(fieldName)
        repeaterItems.push(...normalizedItems)
      }

      repeaterTables[fieldName] = await buildBillingTable(normalizedItems, {
        resolveTaxRate,
        currencySymbol,
      })

      if (includeRepeaterInFinancial) {
        financialItems.push(...normalizedItems)
      }
    } else if (field.category === 'financial') {
      const mapped = mapFinancialFieldToItems(fieldName, field, receipt, fieldsUsed)
      financialItems.push(...mapped)
    }
  }

  const financialTable = await buildBillingTable(financialItems, {
    resolveTaxRate,
    currencySymbol,
  })

  const combinedSource = includeRepeaterInFinancial
    ? financialItems
    : [...repeaterItems, ...financialItems]
  const combinedTable = await buildBillingTable(combinedSource, {
    resolveTaxRate,
    currencySymbol,
  })
  return {
    repeaterTables,
    financialTable,
    combinedTable,
    fieldsUsedInCombined: Array.from(fieldsUsed),
  }
}
